import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import ScanReceipt from './pages/ScanReceipt';
import AddIncome from './pages/AddIncome';
import Transactions from './pages/Transactions';
import Analytics from './pages/Analytics';
import Subscription from './pages/Subscription';
import Admin from './pages/Admin';
import SmartRules from './pages/SmartRules';
import ReceiptsGallery from './pages/ReceiptsGallery';
import BudgetLimits from './pages/BudgetLimits';
import ResetPassword from './pages/ResetPassword';
import Categories from './pages/Categories';
import Settings from './pages/Settings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import VerifyEmail from './pages/VerifyEmail';
import Support from './pages/Support';
import HowToUse from './pages/HowToUse';
import Invoices from './pages/Invoices';
import TaxCalculator from './pages/TaxCalculator';
// SEO Landing Pages
import ElectriciansLanding from './pages/ElectriciansLanding';
import Contractors1099Landing from './pages/Contractors1099Landing';
import FreelancersLanding from './pages/FreelancersLanding';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/auth" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/dashboard" replace />;
};

const AppRouter = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  // Check for Google OAuth callback (session_id in URL fragment)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // List of pages where banner should not show
  const noBannerPages = ['/', '/auth', '/verify-email', '/reset-password', '/privacy-policy', '/electricians', '/contractors', '/1099', '/freelancers', '/blog'];
  const showBanner = user && !noBannerPages.includes(location.pathname) && !location.pathname.startsWith('/blog/');

  return (
    <>
      {showBanner && <EmailVerificationBanner />}
      <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <Landing />
          </PublicRoute>
        }
      />
      <Route
        path="/auth"
        element={
          <PublicRoute>
            <Auth />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />
      <Route
        path="/privacy-policy"
        element={<PrivacyPolicy />}
      />
      <Route
        path="/verify-email"
        element={<VerifyEmail />}
      />

      {/* SEO Landing Pages - Public */}
      <Route path="/electricians" element={<ElectriciansLanding />} />
      <Route path="/contractors" element={<ElectriciansLanding />} />
      <Route path="/1099" element={<Contractors1099Landing />} />
      <Route path="/freelancers" element={<FreelancersLanding />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* Private Routes */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/scan"
        element={
          <PrivateRoute>
            <ScanReceipt />
          </PrivateRoute>
        }
      />
      <Route
        path="/add-income"
        element={
          <PrivateRoute>
            <AddIncome />
          </PrivateRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <PrivateRoute>
            <Transactions />
          </PrivateRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <PrivateRoute>
            <Analytics />
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <PrivateRoute>
            <Invoices />
          </PrivateRoute>
        }
      />
      <Route
        path="/tax-calculator"
        element={
          <PrivateRoute>
            <TaxCalculator />
          </PrivateRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <PrivateRoute>
            <Subscription />
          </PrivateRoute>
        }
      />
      <Route
        path="/smart-rules"
        element={
          <PrivateRoute>
            <SmartRules />
          </PrivateRoute>
        }
      />
      <Route
        path="/receipts-gallery"
        element={
          <PrivateRoute>
            <ReceiptsGallery />
          </PrivateRoute>
        }
      />
      <Route
        path="/budget-limits"
        element={
          <PrivateRoute>
            <BudgetLimits />
          </PrivateRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <PrivateRoute>
            <Categories />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/support"
        element={
          <PrivateRoute>
            <Support />
          </PrivateRoute>
        }
      />
      <Route
        path="/how-to-use"
        element={
          <PrivateRoute>
            <HowToUse />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <Admin />
          </PrivateRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppRouter />
            <PWAInstallPrompt />
            <Toaster position="top-right" theme="dark" richColors />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
